"use client";

import * as React from "react";
import { cn } from "@/shared/core/utils";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/presentation/components/ui";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  footer?: React.ReactNode;
}

/**
 * Modal component for dialogs and overlays
 * Uses shadcn Dialog for accessibility and smooth animations
 * Provides a consistent modal experience across the app
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  className,
  contentClassName,
  showCloseButton = true,
  closeOnOverlayClick = true,
  footer,
}: ModalProps) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && closeOnOverlayClick) {
          onClose();
        }
      }}
    >
      <DialogContent
        className={cn("max-h-[90vh] flex flex-col", contentClassName)}
        showCloseButton={showCloseButton}
        onPointerDownOutside={(e) => {
          if (!closeOnOverlayClick) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!closeOnOverlayClick) {
            e.preventDefault();
          }
        }}
      >
        {/* Header */}
        {title && (
          <DialogHeader className={className}>
            <DialogTitle className="text-xl font-bold">{title}</DialogTitle>
          </DialogHeader>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto">{children}</div>

        {/* Footer */}
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Modal for displaying iframe content (Terms, Privacy Policy)
 */
interface IframeModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  src: string;
  onAgree?: () => void;
  onPrint?: () => void;
  agreeText?: string;
  printText?: string;
}

export function IframeModal({
  isOpen,
  onClose,
  title,
  src,
  onAgree,
  onPrint,
  agreeText = "Agree",
  printText = "Print / Save as PDF",
}: IframeModalProps) {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  const handlePrint = () => {
    const win = iframeRef.current?.contentWindow;
    if (win) {
      win.focus();
      win.print();
    } else if (iframeRef.current?.src) {
      const popup = window.open(iframeRef.current.src, "_blank");
      popup?.focus();
      try {
        popup?.print();
      } catch {
        // Ignore print errors
      }
    }
    onPrint?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      contentClassName="max-w-4xl h-[90vh]"
      footer={
        <>
          {onPrint !== undefined && (
            <Button variant="outline" onClick={handlePrint}>
              {printText}
            </Button>
          )}
          {onAgree && <Button onClick={onAgree}>{agreeText}</Button>}
        </>
      }
    >
      <iframe
        ref={iframeRef}
        src={src}
        title={title}
        className="h-full w-full border-0"
      />
    </Modal>
  );
}

export default Modal;
